/**
 * Stale-chunk auto-recovery.
 *
 * When a new deploy ships, hashed chunk filenames change. Browsers with the
 * pre-deploy `index.html` still in memory try to fetch the OLD chunk URLs.
 * The SPA fallback serves `index.html` for unknown paths -> browser refuses
 * `text/html` as a JS module -> `lazy()` rejects -> ErrorBoundary fires.
 *
 * Recovery ladder (one step at a time):
 *   1. First chunk-load error this session -> hard reload. Fresh index.html
 *      pulls in the new chunk hashes and everything resolves.
 *   2. Reload didn't fix it (flag still set, second failure) -> force logout
 *      and hard-redirect to /auth/login. State on the current page is
 *      irrecoverable; a clean re-auth is the safest reset.
 *   3. Reload-flag clears after ~10s of healthy uptime so a LATER deploy can
 *      still self-heal without skipping straight to logout.
 */

const RELOAD_FLAG = 'medicodio:chunk-reloaded'
const HEALTHY_UPTIME_MS = 10_000

const CHUNK_ERROR_PATTERNS: RegExp[] = [
  /Failed to fetch dynamically imported module/i,
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk/i,
  /Importing a module script failed/i,
  /Expected a JavaScript.*module/i,
  /error loading dynamically imported module/i,
]

function isChunkLoadError(msg: string): boolean {
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(msg))
}

function extractMessage(err: unknown): string {
  if (!err) return ''
  if (typeof err === 'string') return err
  if (err instanceof Error) return err.message
  if (typeof err === 'object' && err !== null && 'message' in err) {
    return String((err as { message?: unknown }).message ?? '')
  }
  return String(err)
}

function tryReload(reason: string): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) {
      console.error(`[chunk-reload] Reload already attempted; falling back to forced logout. reason=${reason}`)
      forceLogoutFallback(reason)
      return false
    }
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()))
  } catch {
    // sessionStorage unavailable (private mode / quota) — reload anyway, but only once per page-load.
  }
  console.warn(`[chunk-reload] Stale chunk detected, reloading. reason=${reason}`)
  window.location.reload()
  return true
}

/**
 * Last-resort recovery: stale-chunk error survived a reload. Some state on the
 * page is irrecoverably tied to dead bundle hashes. Wipe local auth state and
 * hard-redirect to /auth/login. Uses sendBeacon for the server-side logout so
 * the call doesn't depend on the axios chunk (which may itself be the stale one).
 */
function forceLogoutFallback(reason: string) {
  console.error(`[chunk-reload] Forcing logout due to unrecoverable stale-chunk error: ${reason}`)
  // Skip the loop guard so a future page also gets this treatment if it happens
  // again later. The redirect below will reach /auth/login regardless.
  try { sessionStorage.removeItem(RELOAD_FLAG) } catch { /* ignore */ }

  // Best-effort server-side session invalidation. sendBeacon does NOT need a
  // working JS chunk graph — it ships from the browser's networking stack.
  try {
    navigator.sendBeacon?.('/api/v1/app/auth/logout', new Blob([], { type: 'application/json' }))
  } catch { /* ignore */ }

  // Local auth state cleanup. Mirrors what `forceLogout()` in use-auth.ts does,
  // but inlined here to avoid pulling the auth module (and its transitive deps)
  // into the entry bundle.
  try { localStorage.removeItem('auth-perms-cache') } catch { /* ignore */ }

  // Hard navigate — bypasses React entirely. `.replace` so the broken state
  // doesn't sit in browser history.
  if (!window.location.pathname.startsWith('/auth/')) {
    window.location.replace('/auth/login?reason=stale_chunk')
  } else {
    window.location.reload()
  }
}

export function installChunkReloadHandler() {
  if (typeof window === 'undefined') return

  // Vite-emitted preload error — fires for both <link rel="modulepreload"> and
  // dynamic import() failures. Calling preventDefault stops the error from
  // bubbling to React.
  window.addEventListener('vite:preloadError', (event: Event) => {
    const payload = (event as Event & { payload?: unknown }).payload
    const msg = extractMessage(payload) || 'vite:preloadError'
    if (tryReload(msg)) event.preventDefault()
  })

  // Fallback for environments / browsers where vite:preloadError is missed.
  // Watches unhandled promise rejections (React lazy rejections land here when
  // not caught by an ErrorBoundary tree).
  window.addEventListener('unhandledrejection', (event) => {
    const msg = extractMessage(event.reason)
    if (msg && isChunkLoadError(msg)) {
      if (tryReload(msg)) event.preventDefault()
    }
  })

  // After a stable boot, clear the per-session reload flag so a LATER deploy
  // can still self-heal. Without this, only one stale-chunk recovery would
  // ever happen per browser session.
  window.setTimeout(() => {
    try { sessionStorage.removeItem(RELOAD_FLAG) } catch { /* ignore */ }
  }, HEALTHY_UPTIME_MS)
}
